import BaseComponent from '../../BaseComponent';

type Decision = {locale: string | null};
type Target = HTMLInputElement | HTMLTextAreaElement;

export default class LanguageDetection extends BaseComponent {
    private readonly eventNamespace = `.language-detection-${Math.random().toString(36).slice(2)}`;
    private $form: JQuery<HTMLElement>;
    private targetSelector = '';
    private buttonsSelector = '';
    private readonly disabledButtons = new Map<HTMLButtonElement | HTMLInputElement, boolean>();
    private url: string | null = null;
    private timer: number | null = null;
    private revision = 0;
    private disposed = false;
    private decided = false;
    private proposedLocale: string | null = null;
    private proposedTargets: Target[] | null = null;
    private lastText = '';

    private readonly afterSnippetUpdate = (): void => {
        if (!this.proposedLocale) {
            return;
        }
        if (!this.proposedTargets || !this.isCurrent(this.proposedTargets, this.revision)) {
            this.hidePrompt();
            return;
        }
        this.disableButtons();
    };

    public init(): void {
        this.$form = this.$component.closest('form');
        this.targetSelector = this.$component.attr('data-target') || '';
        this.buttonsSelector = this.$component.attr('data-buttons-to-disable') || '';
        this.url = document.querySelector<HTMLMetaElement>('meta[name="language-detection-url"]')?.content || null;
        const field = this.sourceField();
        if (!this.$form.length || !this.targetSelector || !field) {
            return;
        }

        const decision = this.decisions()[this.decisionKey()];
        this.decided = Boolean(field.value || decision);
        if (decision?.locale) {
            field.value = decision.locale;
        }
        const targets = this.targets();
        this.lastText = this.combinedText(targets);

        this.$form.on(`input${this.eventNamespace} editor-change${this.eventNamespace}`, this.targetSelector, event => {
            const target = event.currentTarget as Target;
            const detail = (event.originalEvent as CustomEvent<string>)?.detail;
            this.changed(target, typeof detail === 'string' ? detail : target.value);
        });
        this.$component.on(`click${this.eventNamespace}`, '[data-language-detection-confirm]', () => this.answer(true));
        this.$component.on(`click${this.eventNamespace}`, '[data-language-detection-reject]', () => this.answer(false));
        if (this.buttonsSelector) {
            this.context.naja.snippetHandler.addEventListener('afterUpdate', this.afterSnippetUpdate);
        }
        if (this.$component.attr('data-detect-on-init') === 'true' && targets.length) {
            const target = targets[0];
            this.changed(target, window.ckeditors?.[target.id]?.getData() ?? target.value, true);
        }
    }

    public beforeRemove(): void {
        this.disposed = true;
        this.revision++;
        this.clearTimer();
        this.hidePrompt();
        if (this.buttonsSelector) {
            this.context.naja.snippetHandler.removeEventListener('afterUpdate', this.afterSnippetUpdate);
        }
        this.$form.off(this.eventNamespace);
        this.$component.off(this.eventNamespace);
    }

    private changed(target: Target, content: string, force = false): void {
        if (this.decided || this.disposed || target.disabled || target.readOnly) {
            return;
        }

        const targets = this.targets();
        const text = this.combinedText(targets, target, content);
        if (!force && text === this.lastText) {
            return;
        }
        this.lastText = text;
        this.revision++;
        this.clearTimer();
        this.hidePrompt();
        if (Array.from(text).length < 60) {
            return;
        }

        const revision = this.revision;
        if (force) {
            void this.detect(targets, text, revision);
            return;
        }
        this.timer = window.setTimeout(() => {
            this.timer = null;
            void this.detect(targets, text, revision);
        }, 500);
    }

    private async detect(targets: Target[], text: string, revision: number): Promise<void> {
        if (!this.url || !this.isCurrent(targets, revision)) {
            return;
        }

        try {
            const payload = await this.context.naja.makeRequest('POST', this.url,
                JSON.stringify({text: Array.from(text).slice(0, 5000).join('')}), {
                    history: false,
                    abort: false,
                    unique: false,
                    contentTranslation: true,
                    fetch: {headers: {'Content-Type': 'application/json'}},
                },
            );
            if (!this.isCurrent(targets, revision) || typeof payload?.locale !== 'string' || !payload.locale
                || typeof payload?.languageName !== 'string' || !payload.languageName
                || this.language(payload.locale) === this.language(this.translator.locale)) {
                return;
            }

            this.proposedLocale = payload.locale;
            this.proposedTargets = targets;
            const $question = this.$component.find('[data-language-detection-question]');
            $question.text(($question.attr('data-message') || '').split('%language%').join(payload.languageName));
            this.$component.show();
            this.disableButtons();
        } catch {
            // Detection is optional and must not interfere with editing or submitting the form.
        }
    }

    private isCurrent(targets: Target[], revision: number): boolean {
        const currentTargets = this.targets();
        return !this.disposed && !this.decided && this.revision === revision
            && this.$component[0].isConnected && targets.length > 0
            && currentTargets.length === targets.length
            && targets.every((target, index) => currentTargets[index] === target && !target.disabled && !target.readOnly)
            && !this.sourceField()?.value;
    }

    private answer(confirmed: boolean): void {
        if (this.decided || !this.proposedLocale) {
            return;
        }
        if (!this.proposedTargets || !this.isCurrent(this.proposedTargets, this.revision)) {
            this.hidePrompt();
            return;
        }
        const locale = confirmed ? this.proposedLocale : null;
        const field = this.sourceField();
        if (locale && field) {
            field.value = locale;
        }
        this.decisions()[this.decisionKey()] = {locale};
        this.decided = true;
        this.revision++;
        this.clearTimer();
        this.hidePrompt();
    }

    private sourceField(): HTMLInputElement | undefined {
        return this.$form.find<HTMLInputElement>(this.$component.attr('data-source-locale') || '').get(0);
    }

    private targets(): Target[] {
        return this.$form.find<Target>(this.targetSelector).filter('input, textarea').toArray();
    }

    private combinedText(targets: Target[], changedTarget?: Target, changedContent?: string): string {
        const content = targets.map(target => target === changedTarget
            ? changedContent
            : (window.ckeditors?.[target.id]?.getData() ?? target.value));
        return this.plainText(content.join(' '));
    }

    private decisionKey(): string {
        const field = this.sourceField();
        return field?.id || field?.name || 'sourceLocale';
    }

    private decisions(): Record<string, Decision> {
        let decisions = this.$form.data('languageDetectionDecisions') as Record<string, Decision> | undefined;
        if (!decisions) {
            decisions = {};
            this.$form.data('languageDetectionDecisions', decisions);
        }
        return decisions;
    }

    private clearTimer(): void {
        if (this.timer !== null) {
            window.clearTimeout(this.timer);
            this.timer = null;
        }
    }

    private hidePrompt(): void {
        this.proposedLocale = null;
        this.proposedTargets = null;
        this.$component.hide();
        this.disabledButtons.forEach((disabled, button) => {
            button.disabled = disabled;
        });
        this.disabledButtons.clear();
    }

    private disableButtons(): void {
        if (!this.buttonsSelector) {
            return;
        }
        this.$form.find<HTMLButtonElement | HTMLInputElement>(this.buttonsSelector)
            .filter('button, input[type="submit"], input[type="button"], input[type="reset"]')
            .each((_, button) => {
                if (this.$component[0].contains(button)) {
                    return;
                }
                if (!this.disabledButtons.has(button)) {
                    this.disabledButtons.set(button, button.disabled);
                }
                button.disabled = true;
            });
    }

    private language(locale: string): string {
        const language = locale.toLowerCase().split(/[_-]/)[0];
        return language === 'nb' ? 'no' : language;
    }

    private plainText(content: string): string {
        if (this.$component.attr('data-content-format') !== 'html') {
            return content.trim();
        }

        const template = document.createElement('template');
        template.innerHTML = content;
        template.content.querySelectorAll('script, style, template').forEach(element => element.remove());
        template.content.querySelectorAll('address, article, aside, blockquote, br, div, dl, dt, dd, fieldset, figcaption, figure, footer, h1, h2, h3, h4, h5, h6, header, hr, li, main, nav, ol, p, pre, section, table, tbody, td, th, thead, tr, ul').forEach(element => {
            element.before(document.createTextNode(' '));
            element.after(document.createTextNode(' '));
        });
        return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
    }
}
